import { FunctionComponent, useRef, useState } from "react";
import {
    Button,
    Checkbox,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
} from "@chakra-ui/react";

export interface SavePresetModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (name: string, isDefault: boolean) => Promise<void>;
    isSaving: boolean;
}

export const SavePresetModal: FunctionComponent<SavePresetModalProps> = ({ isOpen, onClose, onSave, isSaving }) => {
    const [name, setName] = useState("");
    const [isDefault, setIsDefault] = useState(false);
    const [nameError, setNameError] = useState<string | null>(null);
    const initialFocusRef = useRef<HTMLInputElement>(null);

    const handleClose = () => {
        setName("");
        setIsDefault(false);
        setNameError(null);
        onClose();
    };

    const handleSave = async () => {
        if (!name.trim()) {
            setNameError("Preset name is required.");
            return;
        }
        setNameError(null);
        await onSave(name.trim(), isDefault);
        setName("");
        setIsDefault(false);
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} initialFocusRef={initialFocusRef} isCentered>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Save Current Filters as Preset</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <FormControl isInvalid={!!nameError} mb={4}>
                        <FormLabel>Preset Name</FormLabel>
                        <Input
                            ref={initialFocusRef}
                            placeholder="e.g. Active Pending Review"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            maxLength={100}
                            onKeyDown={e => {
                                if (e.key === "Enter") handleSave();
                            }}
                        />
                        {nameError && <FormErrorMessage>{nameError}</FormErrorMessage>}
                    </FormControl>
                    <Checkbox isChecked={isDefault} onChange={e => setIsDefault(e.target.checked)}>
                        Set as default (auto-applied when grid loads)
                    </Checkbox>
                </ModalBody>
                <ModalFooter>
                    <Button variant="ghost" mr={3} onClick={handleClose} isDisabled={isSaving}>
                        Cancel
                    </Button>
                    <Button colorScheme="blue" onClick={handleSave} isLoading={isSaving}>
                        Save Preset
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};
